import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import Reveal from "@/components/Reveal";
import PracticeCard from "@/components/PracticeCard";
import WhyHire from "@/components/WhyHire";
import Seo from "@/components/Seo";
import { PRACTICE_AREAS, FIRM, IMAGES } from "@/lib/site";
import { FinalCTA } from "./Home";

export default function PracticeAreas() {
  return (
    <div data-testid="practice-areas-page" className="bg-white">
      <Seo
        title="Practice Areas | Massachusetts Trial Attorney — Mahaney Law"
        description="Personal injury, motor vehicle accidents, truck accidents, slip and fall injuries, and criminal defense — handled directly by Attorney Joseph M. Mahaney across Massachusetts."
        path="/practice-areas"
      />

      {/* Header — centered over courthouse backdrop with practice pills */}
      <section data-testid="practice-hero" className="relative overflow-hidden bg-navy-950 md:flex md:items-center md:min-h-[calc(100vh-6rem)]">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-[0.45]"
          style={{ backgroundImage: `url(${IMAGES.courthouse})` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-navy-950/55" />
        <div className="relative w-full max-w-5xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-16 text-center">
          <Reveal>
            <div className="eyebrow text-gold">Practice Areas</div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-[4.5rem] leading-[1.05] tracking-tight text-white mt-5 text-balance">
              Focused practice areas,
              <span className="block italic font-medium text-gold">
                handled by trial counsel.
              </span>
            </h1>
            <p className="mt-7 mx-auto max-w-2xl text-lg md:text-[1.15rem] text-white/70 leading-relaxed font-light">
              Mahaney Law concentrates on the matters where 40+ years of trial
              experience make a measurable difference. Every case is handled
              directly by Attorney Mahaney — {FIRM.serviceArea.toLowerCase()}.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-2.5">
              {PRACTICE_AREAS.map((a) => (
                <span
                  key={a.slug}
                  data-testid={`practice-hero-pill-${a.slug}`}
                  className="px-4 py-2 rounded-full border border-white/15 bg-white/[0.04] text-white/75 text-[0.7rem] uppercase tracking-[0.14em] font-semibold"
                >
                  {a.title}
                </span>
              ))}
            </div>
            <div className="mt-9 flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/contact"
                data-testid="practice-list-consult"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-sm bg-gold text-navy-950 text-sm font-semibold hover:bg-gold-dark transition-all"
              >
                Free Consultation
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={`tel:${FIRM.phoneHref}`}
                data-testid="practice-list-call"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-sm border-2 border-white/30 text-white text-sm font-semibold hover:bg-white hover:text-navy-950 transition-all"
              >
                <Phone className="h-4 w-4 text-gold" />
                Call Now
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Grid of practice areas */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PRACTICE_AREAS.map((area, i) => (
              <Reveal key={area.slug} delay={i * 0.05}>
                <PracticeCard area={area} index={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why hire */}
      <section className="bg-navy-50 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <WhyHire />
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
